import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SideNav from "../../../components/header/SideNav";
import TopNav from "../../../components/header/TopNav";
import data from "./data";

const ScienceExamQuestion = () => {
  const navigate = useNavigate();
  const [show, setShow] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    score: 0,
    subject: "science",
  });
  const [selectedAnswers, setSelectedAnswers] = useState({});

  const calculateScore = () => {
    let score = 0;
    questionSet.questions.forEach((question, index) => {
      if (selectedAnswers[index] === question.correctAnswer) {
        score++;
      }
    });
    return score;
  };

  const handleShow = () => {
    setShow(!show);
  };

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    const storedIndex = localStorage.getItem("currentQuestionIndex");
    return storedIndex ? parseInt(storedIndex) : 0;
  });
  const [remainingTime, setRemainingTime] = useState(60);
  const [selectedOption, setSelectedOption] = useState(null);

  const randomizeQuestionSet = useCallback(() => {
    const storedSetIndex = localStorage.getItem("setIndex");
    if (storedSetIndex) {
      return parseInt(storedSetIndex);
    } else {
      const newSetIndex = Math.floor(Math.random() * data.length);
      localStorage.setItem("setIndex", newSetIndex);
      return newSetIndex;
    }
  }, []);

  const [setIndex] = useState(() => randomizeQuestionSet());
  const [questionSet] = useState(() => data[setIndex]);

  const handleNextQuestion = useCallback(() => {
    if (currentQuestionIndex < questionSet.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setRemainingTime(60);
      setSelectedOption(null);
      localStorage.setItem("startTime", Date.now());
      localStorage.setItem("currentQuestionIndex", currentQuestionIndex + 1);
    } else {
      const finalScore = calculateScore();
      setFormData((prev) => ({ ...prev, score: finalScore }));
      setShowModal(true);
    }
  }, [currentQuestionIndex, questionSet.questions.length]);

  useEffect(() => {
    const startTime = localStorage.getItem("startTime");
    const elapsedTime = startTime
      ? Math.floor((Date.now() - parseInt(startTime)) / 1000)
      : 0;
    const initialRemainingTime = 60 - elapsedTime;

    if (initialRemainingTime > 0) {
      setRemainingTime(initialRemainingTime);
    } else {
      handleNextQuestion();
    }

    const timer = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime > 1) {
          return prevTime - 1;
        } else {
          handleNextQuestion();
          return 0;
        }
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [handleNextQuestion]);

  const handleOptionChange = (option) => {
    setSelectedOption(option);
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestionIndex]: option,
    });
  };

  const currentQuestion = questionSet.questions[currentQuestionIndex];

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        "https://learninghammer.hammerlabcreations.com/api/score/post",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        alert("Failed to submit. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting:", error);
      alert("Failed to submit. Please try again.");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    // Clear local storage
    localStorage.removeItem("currentQuestionIndex");
    localStorage.removeItem("setIndex");
    localStorage.removeItem("startTime");
    // Navigate to home page
    navigate("/");
  };

  return (
    <div className="home_section">
      <div className={show ? "show" : "hidden"}>
        <SideNav />
        <div className="overlay" onClick={handleShow}></div>
      </div>
      <div className="right">
        <TopNav handleShow={handleShow} show={show} />
        <div className="home_container p-3 p-lg-4">
          <div className="questions">
            <div className="question">
              <div className="">
                <h3>Set Number: {setIndex + 1}</h3>
                <h5 className="text-2xl mb-3">
                  Attempt all the questions and darken your answer in the given
                  answer sheet.
                </h5>
                <h3>Remaining Time: {remainingTime}s</h3>
              </div>
              <div className="mt-5">
                <h4 className="text-dark mb-4 text-bold">
                  {currentQuestionIndex + 1}. {currentQuestion.question}
                </h4>
                <div className="options_container">
                  {currentQuestion.options.map((option, index) => (
                    <div key={index} className="option_item">
                      <input
                        type="radio"
                        id={`q${currentQuestionIndex}_option${index}`}
                        name={`question${currentQuestionIndex}`}
                        value={option}
                        checked={selectedOption === option}
                        onChange={() => handleOptionChange(option)}
                      />
                      &nbsp;
                      <label
                        htmlFor={`q${currentQuestionIndex}_option${index}`}
                        className="text-bold"
                      >
                        {String.fromCharCode(65 + index)}. {option}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={handleNextQuestion} className="next_button">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isSubmitted
                    ? "You have completed your exams"
                    : "Submit Your Details"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body">
                {!isSubmitted ? (
                  <form onSubmit={handleFormSubmit}>
                    <div className="mb-3">
                      <label htmlFor="name" className="form-label">
                        Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="name"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="phone" className="form-label">
                        Phone
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        id="phone"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                        required
                      />
                    </div>
                    <button type="submit" className="btn btn-primary">
                      Submit
                    </button>
                  </form>
                ) : (
                  <div className="text-center">
                    <h4>We will contact you soon with your scores</h4>
                    <button
                      className="btn btn-primary mt-3"
                      onClick={handleCloseModal}
                    >
                      Return to Home
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScienceExamQuestion;
